import { render, $ } from "../lib/ui";
import { icons } from "../lib/icons";
import type { Quality, QualityLevel } from "../lib/quality";

export interface InCallHandlers {
  peerCode: string;
  localStream: MediaStream;
  hasLocalVideo: boolean;
  onToggleMic: (on: boolean) => void;
  onToggleCam: (on: boolean) => void;
  onHangup: () => void;
}

export interface InCallView {
  setRemoteStream: (stream: MediaStream) => void;
  setStatus: (text: string) => void;
  setQuality: (q: Quality | null) => void;
}

const QUALITY_LABEL: Record<QualityLevel, string> = {
  good: "Good connection",
  medium: "Weak connection",
  poor: "Poor connection",
};

export function showInCall({
  peerCode,
  localStream,
  hasLocalVideo,
  onToggleMic,
  onToggleCam,
  onHangup,
}: InCallHandlers): InCallView {
  render(`
    <div class="screen incall">
      <div class="call-bg"></div>
      <video class="video-remote" id="remote" autoplay playsinline></video>
      <div class="local-tile" id="local-tile">
        <video class="video-local" id="local" autoplay playsinline muted></video>
        <div class="local-off" id="local-off" hidden>
          ${icons.camOff}
          <span>Camera off</span>
        </div>
      </div>
      <div class="topbar">
        <div class="peer-name">${peerCode}</div>
        <div class="conn">
          <span class="quality-dot" id="dot" hidden></span>
          <span id="status">Connecting…</span>
        </div>
      </div>
      <div class="controls">
        <button class="ctrl" id="mic" title="Mute">${icons.micOn}</button>
        <button class="ctrl" id="cam" title="Camera">${icons.camOn}</button>
        <button class="ctrl end" id="hangup" title="End call">${icons.hangup}</button>
      </div>
    </div>
  `);

  const localVideo = $("#local") as HTMLVideoElement;
  const remoteVideo = $("#remote") as HTMLVideoElement;
  const localOff = $("#local-off");
  const dot = $("#dot");
  localVideo.srcObject = localStream;

  // Show the "Camera off" card in the self-view tile when our camera is off.
  const setLocalCam = (on: boolean) => {
    localVideo.style.visibility = on ? "visible" : "hidden";
    localOff.hidden = on;
  };

  let micOn = true;
  let camOn = hasLocalVideo;

  const micBtn = $("#mic");
  micBtn.addEventListener("click", () => {
    micOn = !micOn;
    micBtn.classList.toggle("off", !micOn);
    micBtn.innerHTML = micOn ? icons.micOn : icons.micOff;
    onToggleMic(micOn);
  });

  const camBtn = $("#cam") as HTMLButtonElement;
  if (!hasLocalVideo) {
    // No camera on this device — locked off, show the off card.
    camBtn.disabled = true;
    camBtn.classList.add("off");
    camBtn.innerHTML = icons.camOff;
    camBtn.title = "No camera found";
    setLocalCam(false);
  } else {
    camBtn.addEventListener("click", () => {
      camOn = !camOn;
      camBtn.classList.toggle("off", !camOn);
      camBtn.innerHTML = camOn ? icons.camOn : icons.camOff;
      setLocalCam(camOn);
      onToggleCam(camOn);
    });
  }

  $("#hangup").addEventListener("click", onHangup);

  // If the remote has no camera (audio only), hide the black video element so
  // the calm backdrop shows instead.
  const updateRemoteVideo = (stream: MediaStream) => {
    const track = stream.getVideoTracks()[0];
    const show = !!track && track.enabled && !track.muted;
    remoteVideo.style.visibility = show ? "visible" : "hidden";
  };

  return {
    setRemoteStream: (stream) => {
      remoteVideo.srcObject = stream;
      updateRemoteVideo(stream);
      const track = stream.getVideoTracks()[0];
      if (track) {
        track.onmute = () => updateRemoteVideo(stream);
        track.onunmute = () => updateRemoteVideo(stream);
        track.onended = () => updateRemoteVideo(stream);
      }
    },
    setStatus: (text) => {
      $("#status").textContent = text;
    },
    setQuality: (q) => {
      if (!q) {
        dot.hidden = true;
        return;
      }
      dot.hidden = false;
      dot.className = `quality-dot q-${q.level}`;
      dot.title = QUALITY_LABEL[q.level] + (q.relayed ? " (relayed)" : "");
    },
  };
}
